/**
 * AdvisorFAQ Component
 *
 * This component renders a list of frequently asked questions (FAQs) for advisors.
 * Each FAQ item is collapsible, allowing users to toggle visibility of the answers.
 *
 * 📌 Features:
 * - Expand/collapse functionality for each FAQ.
 * - Dynamic arrow icon (`▶` for closed, `▼` for open).
 * - Modular design to be used inside the CustomerServicePopup.
 *
 * 📂 Related Files:
 * - `AdvisorFAQ.module.scss` (styles)
 * - `CustomerServicePopup.tsx` (parent component)

 * @returns {JSX.Element} The Advisor FAQ section.
 */

import React, { useState } from "react";
import ChevronRightIcon from "@js/component/icon/ChevronRightIcon";
import ChevronDownIcon from "@js/component/icon/ChevronDownIcon";
import * as classNames from "@css/component/AdvisorFAQ.module.scss";


const AdvisorFAQ = () => {
    const faqItems = [
        {
            question: "Hoe werkt Woninglabel.nl?",
            answer: (
                <>
                    Woninglabel.nl maakt het{" "}
                    <a href="https://woninglabel.nl/" target="_blank" rel="noopener noreferrer">
                        aanvragen van een energielabel en/of maatwerkadvies
                    </a>{" "}
                    eenvoudig. Je vraagt online een energielabel en/of maatwerkadvies aan en wordt gekoppeld aan een{" "}
                    <a href="https://woninglabel.nl/over-ons/adviseurs" target="_blank" rel="noopener noreferrer">
                        erkende energieadviseur
                    </a>
                    . De adviseur komt vervolgens bij jou langs om het gebouw te inspecteren. Zodra het label of advies is vastgesteld,
                    ontvang je deze digitaal en is het energielabel direct zichtbaar in de landelijke {" "}
                    <a href="https://ep-online.nl/" target="_blank" rel="noopener noreferrer">
                        database
                    </a>.
                </>
            )
        },
        {
            question: "Wat is een energielabel?",
            answer: (
                <>
                    Een energielabel geeft aan hoe energiezuinig je woning of bedrijfspand is, op een schaal van
                    {" "} <a href="https://woninglabel.nl/labelnieuws/wat-betekenen-de-energielabels-a-tot-en-met-g" target="_blank" rel="noopener noreferrer"> A+++++ (zeer zuinig) tot G (zeer onzuinig)</a>. Het label is verplicht bij verkoop, verhuur of
                    oplevering van een gebouw en geeft ook advies over mogelijke verbeteringen. Zo krijg je
                    direct inzicht in de energieprestaties én verduurzamingskansen van je woning of
                    bedrijfspand. Via Woninglabel.nl regel je dit snel en eenvoudig met hulp van een erkend adviseur.
                </>
            )
        },
        {
            question: "Waarom een energielabel?",
            answer: (
                <>
                    Een energielabel laat zien hoe energiezuinig je woning of bedrijfspand is en is verplicht bij
                    verkoop, verhuur of oplevering. Het helpt kopers en huurders om in één oogopslag de energieprestaties
                    van een gebouw te beoordelen. Bovendien krijg je inzicht in verbetermogelijkheden om je woning of
                    bedrijfspand energiezuiniger te maken, wat kan leiden tot lagere energiekosten en een beter wooncomfort.
                </>
            )
        },
        {
            question: "Meer weten over energielabels?",
            answer: <>Op onze veelgestelde vragen pagina vind je meer antwoorden op de meest voorkomende {" "}
                <a href="https://woninglabel.nl/over-energielabels/veelgestelde-vragen-over-energielabels/" target="_blank" rel="noopener noreferrer">vragen
                over het energielabel</a> en energielabels in het algemeen.</>
        },
        {
            question: "Wat is een maatwerkadvies?",
            answer: (
                <>
                    Een maatwerkadvies laat zien hoe je jouw woning of bedrijfspand stap voor stap kunt
                    verduurzamen. Je ontvangt een advies met concrete energiebesparende maatregelen, de
                    kosten en besparingen, en een logische volgorde om deze uit te voeren. Zo weet je precies
                    hoe je jouw huis of bedrijfspand energiezuiniger, comfortabeler en toekomstbestendig maakt.
                </>
            )
        },
        {
            question: "Waarom een maatwerkadvies?",
            answer: (
                <>
                    Met een maatwerkadvies krijg je helder inzicht in de beste manier om jouw woning of
                    bedrijfspand te verduurzamen. Het advies laat zien welke maatregelen het meeste
                    opleveren, wat ze kosten en hoe je de maatregelen slim op elkaar afstemt. Zo kun je gericht
                    investeren in energiebesparing, meer comfort en een lagere energierekening.
                </>
            )
        },
        {
            question: "Meer weten over maatwerkadviezen?",
            answer: <>Op onze veelgestelde vragen pagina vind je antwoorden op de meest voorkomende {" "}
                <a href="https://woninglabel.nl/maatwerkadvies/veelgestelde-vragen-over-maatwerkadvies/" target="_blank" rel="noopener noreferrer">vragen
                    over het maatwerkadvies</a> en maatwerkadviezen in het algemeen.</>
        }
    ];
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className={classNames.advisorSection}>
            <h2 className={classNames.faqTitle}>FAQ:</h2>
            <ul className={classNames.faqList}>
                {faqItems.map((item, index) => (
                    <li key={index} className={classNames.faqItem}>
                        <button className={classNames.faqQuestion} onClick={() => toggleFAQ(index)}>
                            <span> {openIndex == index ? <ChevronRightIcon className={classNames.QaIcon}/> : <ChevronDownIcon className={classNames.QaIcon}/>}</span> {item.question}
                        </button>
                        {openIndex === index && <p className={classNames.faqAnswer}>{item.answer}</p>}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AdvisorFAQ;
